/**
 * Creates a equals function to compare objects with a given
 * comparison function between elements
 * @param equalsFn
 */
export function equalsFactory(equalsFn) {
  return (...objects) => {
    if (!objects.length || objects.length === 1) {
      return true;
    }

    const firstObject = objects[0];
    const otherObjects = objects.slice(1);

    if (otherObjects.every((obj) => firstObject === obj)) {
      return true;
    }

    if (!firstObject) {
      return false;
    }

    return otherObjects.every((obj) => {
      if (firstObject === obj) {
        return true;
      }

      if (typeof firstObject !== 'object' || typeof obj !== 'object') {
        return false;
      }

      const objKeys = obj && Object.keys(obj);

      return (
        obj &&
        objKeys.length === Object.keys(firstObject).length &&
        objKeys.every((key) => equalsFn(obj[key], firstObject[key]))
      );
    });
  };
}

/**
 * Compare objects (using === as equality function between elements)
 * @param ...objects
 */
export const shallowEquals = equalsFactory((a, b) => a === b);

/**
 * Omit keys from an object.
 * @param object
 * @param ...keysToOmit
 */
export function omit(object, ...keysToOmit) {
  if (!isObject(object)) {
    throw new Error('`object` param must be an object.');
  }

  const result = { ...object };

  keysToOmit.forEach((key) => delete result[key]);

  return result;
}

/**
 * Get keys from an object.
 * @param object
 * @param ...keysToGet
 */
export function pick(object, ...keysToGet) {
  if (!isObject(object)) {
    throw new Error('`object` param must be an object.');
  }

  const result = {};

  keysToGet.forEach((key) => {
    if (typeof object[key] !== 'undefined') {
      result[key] = object[key];
    }
  });

  return result;
}

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item) {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Deep merge objects.
 * @param target
 * @param ...sources
 */
export function deepMerge(target, ...sources) {
  if (!sources.length) {
    return target;
  }

  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key) => {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }

        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    });
  }

  return deepMerge(target, ...sources);
}

/**
 * Get a property from an object given a nested path
 * @param object
 * @param path
 */
export function getProp(object, propPath) {
  return propPath
    .split('.')
    .reduce((current, path) => current && current[path], object);
}

/**
 * Returns true if object is empty
 * @param object
 */
export function isEmpty(object) {
  if (isObject(object)) {
    return Object.keys(object).length === 0;
  }

  if (Array.isArray(object)) {
    return object.length === 0;
  }

  return false;
}

const getTag = (value) => Object.prototype.toString.call(value);

// removes and reports the first candidate matching, so each one pairs once
const consumeMatch = (candidates, isMatch) => {
  const index = candidates.findIndex(isMatch);

  if (index === -1) {
    return false;
  }

  candidates.splice(index, 1);

  return true;
};

const isObjectLike = (value) =>
  value !== null && (typeof value === 'object' || typeof value === 'function');

function deepEqualWith(a, b, inProgress) {
  if (a === b) {
    return true;
  }

  // like deep-equal's boxed-primitive check: a primitive never equals an
  // object, even when == would coerce them (e.g. 0 == [])
  if (isObjectLike(a) !== isObjectLike(b)) {
    return false;
  }

  // loose like deep-equal's default mode: primitives/nullish compare with ==
  if (!a || !b || (typeof a !== 'object' && typeof b !== 'object')) {
    // eslint-disable-next-line eqeqeq
    return a == b;
  }

  if (typeof a !== typeof b || getTag(a) !== getTag(b)) {
    return false;
  }

  // a pair still being compared further up the stack is assumed equal (cycle
  // guard); it is released once its comparison finishes, so a failed probe
  // can never be remembered as a match
  const seenWithA = inProgress.get(a) || new Set();

  if (seenWithA.has(b)) {
    return true;
  }

  inProgress.set(a, seenWithA.add(b));

  try {
    if (a instanceof Date && a.getTime() !== b.getTime()) {
      return false;
    }

    if (a instanceof RegExp && (a.source !== b.source || a.flags !== b.flags)) {
      return false;
    }

    if (a instanceof Error && (a.name !== b.name || a.message !== b.message)) {
      return false;
    }

    if (a instanceof Map) {
      if (a.size !== b.size) {
        return false;
      }

      // entries whose key is not shared by identity must pair up one-to-one
      const candidates = [...b].filter(([key]) => !a.has(key));

      return [...a].every(([key, value]) =>
        b.has(key)
          ? deepEqualWith(value, b.get(key), inProgress)
          : consumeMatch(
              candidates,
              ([otherKey, otherValue]) =>
                deepEqualWith(key, otherKey, inProgress) &&
                deepEqualWith(value, otherValue, inProgress)
            )
      );
    }

    if (a instanceof Set) {
      if (a.size !== b.size) {
        return false;
      }

      // values not shared by identity must pair up one-to-one
      const candidates = [...b].filter((value) => !a.has(value));

      return [...a].every(
        (value) =>
          b.has(value) ||
          consumeMatch(candidates, (other) =>
            deepEqualWith(value, other, inProgress)
          )
      );
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    return (
      keysA.length === keysB.length &&
      keysA.every(
        (key) =>
          Object.prototype.hasOwnProperty.call(b, key) &&
          deepEqualWith(a[key], b[key], inProgress)
      )
    );
  } finally {
    seenWithA.delete(b);
  }
}

/**
 * Deep equality check replacing the `deep-equal` package (default, non-strict
 * mode): primitives compare with ==, objects by type, own enumerable keys and
 * values recursively (Date, RegExp, Error, Map and Set aware, cycle safe).
 * Prototypes are not compared.
 * @param a
 * @param b
 * @returns {boolean}
 */
export function deepEqual(a, b) {
  return deepEqualWith(a, b, new Map());
}
